import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Loader from '../../../../component/common/Loader';
import SearchInput from '../../../../component/common/Input/SearchInput';
import Table from '../../../../component/common/Table';
import Pagination from '../../../../component/common/table2/Pagination';
import { fetchCompanyGamesUsers } from './companyGamesApi';
import { TableCard } from '../../../../component/common/designSystem';

const PER_PAGE = 10;

/**
 * Searchable people list for the Games section — ENGAGEMENT ONLY.
 *
 * Every column is a count or a date: how many times somebody played, on how
 * many separate days, how many of the four games they have tried, and when they
 * last played. That is the adoption question, and it is a fair one for a
 * company that paid for the benefit to ask.
 *
 * WHAT IS NOT HERE, AND WILL NOT BE: scores, win rates, ranks, levels reached,
 * accuracy. Those exist in this section only as figures about the group, and
 * the backend has no endpoint that would return them per person. There is no
 * row action and no detail page — the arrow most other sections' tables carry
 * is deliberately absent.
 *
 * THERE IS NO LEADERBOARD ANYWHERE IN THIS SECTION, and this table is not one.
 * It sorts by how much people play because that is the useful order for
 * checking take-up, not to rank anybody: the column it sorts on is activity,
 * and no measure of skill appears on the page at all.
 *
 * `isUserGamePrivate` IS HONOURED. Anyone who turned off being listed is absent
 * from these rows and reported as a count instead, so the table never quietly
 * shows fewer people than the department holds.
 *
 * Everybody else in the population is listed, including people who have never
 * opened a game; they show dashes rather than zeros, so "hasn't played" reads
 * differently from "played once and stopped".
 */
const GamesUsersTable = ({
  fetcher = fetchCompanyGamesUsers,
  tableName,
  showCompanyColumn = false,
  department = ''
}) => {
  const [loader, setLoader] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hiddenCount, setHiddenCount] = useState(0);
  const [failed, setFailed] = useState(false);
  const [games, setGames] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(
    async (page, search) => {
      setLoader(true);
      try {
        const response = await fetcher({ page, perPage: PER_PAGE, search, department });
        const gameList = response.games || [];
        const mapped = (response.data || []).map((row) => {
          const active = row.plays > 0;
          const cell = (value) => (active ? Number(value || 0).toLocaleString() : '-');
          const perGame = {};
          gameList.forEach((game) => {
            perGame[`game_${game.key}`] = cell(row.perGame?.[game.key]);
          });
          return {
            ...row,
            ...perGame,
            playsText: cell(row.plays),
            activeDaysText: cell(row.activeDays),
            triedText: active ? `${row.gamesTried} of ${gameList.length}` : '-',
            lastPlayedText: row.lastPlayed || '-'
          };
        });
        setRows(mapped);
        setGames(gameList);
        setTotalCount(response.totalCount || 0);
        setHiddenCount(response.hiddenCount || 0);
        setFailed(false);
      } catch (error) {
        console.error('GamesUsersTable fetchData error:', error);
        setRows([]);
        setTotalCount(0);
        setHiddenCount(0);
        // Kept distinct from an empty result: "nobody matched" and "we could
        // not ask" look identical in a bare table, and only one of them is a
        // fact about your people.
        setFailed(true);
      } finally {
        setLoader(false);
      }
    },
    [fetcher, department]
  );

  // Debounced fetch on search / page / scope change.
  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchData(currentPage, searchTerm);
    }, 200);
    return () => clearTimeout(timeout);
  }, [fetchData, searchTerm, currentPage]);

  const columns = [
    { title: 'Name', key: 'user_profile', sortable: false, align: 'left', type: 'profile' },
    ...(showCompanyColumn
      ? [{ title: 'Company', key: 'company', sortable: false, align: 'center', type: 'text3' }]
      : []),
    { title: 'Played', key: 'playsText', sortable: false, align: 'center', type: 'text3' },
    {
      title: 'Days played',
      key: 'activeDaysText',
      sortable: false,
      align: 'center',
      type: 'text3'
    },
    { title: 'Games tried', key: 'triedText', sortable: false, align: 'center', type: 'text3' },
    // A column per game so "which one did they take to" is answerable without
    // ever saying how well they did at it.
    ...games.map((game) => ({
      title: game.label,
      key: `game_${game.key}`,
      sortable: false,
      align: 'center',
      type: 'text3'
    })),
    { title: 'Last played', key: 'lastPlayedText', sortable: false, align: 'center', type: 'text3' }
  ];

  return (
    <div>
      {loader ? <Loader /> : null}

      <div className="flex justify-end">
        <SearchInput
          id={`${tableName}-search`}
          name={`${tableName}-search`}
          type="text"
          value={searchTerm}
          onChange={(e) => {
            setCurrentPage(1);
            setSearchTerm(e.target.value);
          }}
          placeholder="Search users"
          className="w-full sm:w-auto px-3 py-2"
        />
      </div>

      <TableCard
          pagination={{
            currentPage: currentPage,
            totalCount: totalCount,
            pageSize: PER_PAGE,
            onPageChange: (page) => setCurrentPage(page)
          }}
        >
          <Table
          columns={columns}
          data={rows}
          name={tableName}
          bottomBorder={totalCount > PER_PAGE}
          loader={loader}
        />
        </TableCard>

      

      {failed ? (
        <p className="mt-3 text-xs text-shoorah-secondary dark:text-gray-400">
          This list could not be loaded, so it is empty rather than showing nobody. Refreshing
          usually fixes it.
        </p>
      ) : null}

      {/* Said out loud rather than left as a silently shorter list. */}
      {hiddenCount > 0 ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          {hiddenCount} {hiddenCount === 1 ? 'person has' : 'people have'} turned off being listed
          by name in the games section. They are not shown here, but their play is still counted in
          the figures on the other tabs.
        </p>
      ) : null}

      <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
        Counts and dates only. No score, win rate, rank or level reached appears on this page, and
        there is nothing to open behind a row — how well somebody plays is theirs, that they played
        is what the company is counting.
      </p>
    </div>
  );
};

GamesUsersTable.propTypes = {
  fetcher: PropTypes.func,
  tableName: PropTypes.string,
  showCompanyColumn: PropTypes.bool,
  department: PropTypes.string
};

export default GamesUsersTable;
